import { useVirtualizer } from '@tanstack/react-virtual';

import { useTranslation } from '@mint-lib/i18n';
import { WarningFilled } from '@mint-lib/icons';
import { Checkbox, Skeleton } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type {
  InfinitePaginatedData,
  TableDataFileHeader,
  TableDataFileValidatedRow,
  ValidationSearchParams,
} from '../../api/types.js';
import {
  DEFAULT_HEADER_CELLS_COUNT,
  FIRST_CELL_WIDTH,
  ROW_HEIGHT,
} from '../../constants/tablesConfig.js';
import {
  useHasSufficientViewportHeight,
  useInfiniteScroll,
} from '../../hooks/index.js';
import {
  useChannelsCatalog,
  useEditTableDataFileField,
  usePlatformsCatalog,
} from '../../queries/index.js';
import { TableHeadCellSkeleton } from '../atoms/TableHeadCellSkeleton.jsx';
import { InfinitelyPaginatedTable } from '../molecules/InfinitelyPaginatedTable.jsx';
import { TableBodyValidatedCell } from '../molecules/TableBodyValidatedCell.jsx';
import {
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../molecules/TableComponents.jsx';
import { ValidatedRow } from '../molecules/ValidatedRow.jsx';

const COLUMN_WIDTH = 150;
const PLATFORM_COLUMN_WIDTH = 270;

type DataTableProps = {
  dataFileId: string;
  searchParams: ValidationSearchParams;
  data: InfinitePaginatedData<unknown>;
  headers: TableDataFileHeader[];
  rows: TableDataFileValidatedRow[];
  selection: {
    isInverted: boolean;
    selectedRows: Record<string, boolean>;
  };
  selectedRowsIds: string[];
  handleSelectAllRows: () => void;
  handleSelectRow: (
    rowID: string,
    rowIndex: number,
  ) => (checked: boolean) => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
};

export const DataTable = ({
  dataFileId,
  searchParams,
  data,
  headers,
  rows,
  selection,
  selectedRowsIds,
  handleSelectAllRows,
  handleSelectRow,
  isFetchingNextPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
}: DataTableProps) => {
  const { mutate: editField } = useEditTableDataFileField(
    dataFileId,
    searchParams,
  );

  const { t } = useTranslation('@myn/import-hub');

  /*  Start Virtualization  */
  const threshold = (searchParams.itemsPerPage / 2) * ROW_HEIGHT;

  const scrollableContainer = useInfiniteScroll({
    hasNextPage,
    fetchNextPage,
    threshold,
  });

  // TanStack Virtual returns non-memoizable functions; React Compiler safely skips this component.
  /* oxlint-disable react-hooks-js/incompatible-library */
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => scrollableContainer,
    count: rows.length,
    estimateSize: () => ROW_HEIGHT + 1,
    overscan: searchParams.itemsPerPage,
  });
  /* oxlint-enable react-hooks-js/incompatible-library */

  const virtualRows = rowVirtualizer.getVirtualItems();

  const paddingTop =
    virtualRows.length > 0 ? (virtualRows?.[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0)
      : 0;

  /*  -------------------  */

  const {
    data: { platformsByName },
  } = usePlatformsCatalog();
  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const handleEditField =
    (rowId: string, rowIndex: number, colIndex: number) => (rawValue: string) =>
      editField({
        rowId: rowId,
        payload: {
          columnPosition: headers[colIndex].columnPosition,
          rawValue,
        },
        meta: {
          rowIndex,
          colIndex,
        },
      });

  const isSelectionIndeterminate = Boolean(selectedRowsIds.length);

  const platformColumnIndex = headers.findIndex(
    (header) => header.formatType === 'platform',
  );

  const minTableWidth = headers.reduce(
    (width, _, index) =>
      width +
      (index === platformColumnIndex ? PLATFORM_COLUMN_WIDTH : COLUMN_WIDTH),
    FIRST_CELL_WIDTH,
  );

  const hasSufficientViewportHeight = useHasSufficientViewportHeight();
  const columnsCount = isLoading
    ? DEFAULT_HEADER_CELLS_COUNT + 1
    : headers.length + 1;

  return (
    <InfinitelyPaginatedTable
      columnsCount={columnsCount}
      data={data}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      placeholderRowsCount={50}
      stickyHeader
      className="table-fixed"
      style={{ minWidth: minTableWidth }}
      tableContainerProps={{
        id: 'data-table-container',
        className: cn(
          'min-h-0',
          hasSufficientViewportHeight ? 'shrink' : 'shrink-0',
        ),
      }}
    >
      <TableHead>
        <TableRow>
          <TableHeaderCell className="w-13" skeletonVariant="checkbox">
            {isLoading ? (
              <Skeleton variant="rounded" className="h-3.5 w-3.5" />
            ) : (
              <Checkbox
                data-dd-action-name="Validation: select all rows"
                checked={
                  isSelectionIndeterminate
                    ? 'indeterminate'
                    : selection.isInverted
                }
                onCheckedChange={handleSelectAllRows}
                aria-label={t('Select all rows')}
                className="cursor-pointer"
              />
            )}
          </TableHeaderCell>
          {isLoading
            ? Array.from(
                { length: DEFAULT_HEADER_CELLS_COUNT },
                (_, index) => `header-skeleton-${index}`,
              ).map((skeletonId) => <TableHeadCellSkeleton key={skeletonId} />)
            : headers.map((col, index) => {
                return (
                  <TableHeaderCell
                    key={`${col.headerName}-${col.columnPosition}`}
                    className={cn(
                      'break-words',
                      index === platformColumnIndex && 'w-[270px]',
                    )}
                  >
                    {col.isDisabled ? (
                      <div className="flex items-center gap-2">
                        <WarningFilled
                          role="img"
                          aria-label={t('Deactivated metric')}
                          className="size-4 shrink-0 fill-error-main"
                        />
                        {col.label}
                      </div>
                    ) : (
                      col.label
                    )}
                  </TableHeaderCell>
                );
              })}
        </TableRow>
      </TableHead>
      <TableBody>
        {paddingTop > 0 && (
          <tr>
            <td style={{ height: `${paddingTop}px` }} />
          </tr>
        )}
        {virtualRows.map((virtualRow) => {
          const rowIndex = virtualRow.index;
          const row = rows[rowIndex];
          const platformCell = row.validatedCells[platformColumnIndex];
          return (
            <ValidatedRow
              key={row.id}
              row={row}
              isSelected={
                selection.isInverted
                  ? selection.selectedRows[row.id] !== false
                  : selection.selectedRows[row.id]
              }
              handleSelectRow={handleSelectRow(row.id, rowIndex)}
            >
              {({ cell, colIndex }) => {
                const header = headers[colIndex];
                const isCellValid = !cell.error && !cell.isEdited;

                return (
                  <TableBodyValidatedCell
                    key={`${header.headerName}-${header.columnPosition}`}
                    formatType={header.formatType}
                    cell={cell}
                    platformCell={platformCell}
                    platformsByName={platformsByName}
                    channelsByName={channelsByName}
                    isDisabled={isCellValid || cell.isApiManaged}
                    handleEditField={handleEditField(
                      row.id,
                      rowIndex,
                      colIndex,
                    )}
                  />
                );
              }}
            </ValidatedRow>
          );
        })}
        {paddingBottom > 0 && (
          <tr>
            <td style={{ height: `${paddingBottom}px` }} />
          </tr>
        )}
      </TableBody>
    </InfinitelyPaginatedTable>
  );
};
